import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useCustomStyles } from '../../../hooks';

interface ResponsiveDialogProps extends DialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: React.ReactNode;
    isConfirmationDialog?: boolean;
    onConfirm?: () => void;
    actionButtons?: React.ReactNode;
    // A node, not just a string, so a caller whose label carries a live value can wrap it in
    // `notranslate` — text that updates in place crashes React under browser translation extensions.
    confirmButtonText?: React.ReactNode;
    closeButtonText?: string;
    isLoading?: boolean;
    disableConfirmButton?: boolean;
    /** CALL-818: an X on the title line. Opt-in — for wide/workbench dialogs whose action row is far
     *  from the top; the standard confirmation dialogs keep their Cancel-button-only chrome. */
    showCloseIcon?: boolean;
}

const ResponsiveDialog: React.FC<ResponsiveDialogProps> = ({
    open,
    onClose,
    title,
    children,
    isConfirmationDialog,
    onConfirm,
    actionButtons,
    confirmButtonText,
    closeButtonText,
    isLoading,
    disableConfirmButton,
    showCloseIcon,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const classes = useCustomStyles();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClose = () => {
        onClose();
    };

    return open ? (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
            {...props}
        >
            <DialogTitle id="responsive-dialog-title" sx={showCloseIcon ? { pr: 6 } : undefined}>
                <span>{title}</span>
                {showCloseIcon && (
                    <IconButton
                        aria-label={t('action.close')}
                        onClick={handleClose}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent>{children}</DialogContent>
            {actionButtons ? (
                actionButtons
            ) : (
                <DialogActions sx={isConfirmationDialog ? classes.dialogActions : {}}>
                    {isConfirmationDialog && (
                        <>
                            <Button onClick={handleClose} variant="outlined">
                                <span>{closeButtonText || t('action.cancel')}</span>
                            </Button>
                            <Button
                                onClick={onConfirm}
                                variant="contained"
                                disabled={disableConfirmButton || isLoading}
                            >
                                <span>{confirmButtonText || t('action.confirm')}</span>
                            </Button>
                        </>
                    )}
                    {!isConfirmationDialog && (
                        <Button onClick={handleClose} autoFocus disabled={isLoading}>
                            <span>{t('action.close')}</span>
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    ) : null;
};

export default ResponsiveDialog;
